import InfiniteLoader from "$root/components/InfiniteLoader";
import { authSessionInit, logout } from "$root/functional-areas/user/auth";
import {
	type AuthCallbackFailure,
	classifyAuthCallbackError,
	completeSignin,
	httpStatusFromError,
} from "$root/functional-areas/user/oidc";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AuthCallbackError } from "./AuthCallbackError";

/**
 * Finishes the Cognito Authorization Code + PKCE exchange: completes the OIDC
 * redirect callback, hydrates the app session via `authSessionInit`, then
 * navigates to the originally requested route (carried through Cognito `state`).
 *
 * Serves both `/auth/callback` and `/auth/callback/:emailDomain`. The optional trailing
 * segment is the email domain whose dedicated Cognito pool issued the code; without it the
 * code belongs to the default pool. Cognito redirected here with the same `redirect_uri`
 * the sign-in started from, so the URL alone says which pool to exchange against — nothing
 * has to have survived in this browser's storage. See `completeSignin`.
 */
export function AuthCallback({ emailDomain }: { emailDomain?: string }): React.JSX.Element {
	const history = useHistory();
	const [failure, setFailure] = useState<AuthCallbackFailure | null>(null);

	useEffect(() => {
		(async () => {
			try {
				const { token, from } = await completeSignin(emailDomain);
				await authSessionInit(token);
				history.replace(from);
			} catch (err) {
				console.error("Auth callback failed", err);
				const reason = classifyAuthCallbackError(err);
				trackMixPanelEvent("auth.callbackFailed", { reason, status: httpStatusFromError(err) });
				// Rendered here rather than bounced: logging out navigates — potentially all the way
				// through Cognito's Hosted UI `/logout` — and would destroy the message before it can
				// be read. Since we cannot reproduce these failures ourselves, that message *is* the
				// diagnostic: it is what the user's own report has to carry back to us.
				setFailure(reason);
			}
		})();
	}, [history, emailDomain]);

	if (failure) {
		// The sign-in never completed, so there is no session whose trailing writes could still be
		// authenticated.
		return <AuthCallbackError failure={failure} onBackToSignIn={() => logout({ credentialLost: true })} />;
	}

	return <InfiniteLoader style={{ minHeight: "100dvh" }} />;
}

export default AuthCallback;
